import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    isReturnItemsModalOpen: boolean;
    closeReturnModal: () => void;
    multipleOrders: () => void;
    singleOrder: () => void;
}

type Props = OwnProps;

export interface ADIOrderDetailsReturnItemsModalStyles {
    adiReturnItemsModal?: ModalPresentationProps;
    adiReturnItemsBodyContainer?: InjectableCss;
    adiReturnItemsButtonContainer?: InjectableCss;
    multipleOrdersButtonStyles?: ButtonPresentationProps;
    singleOrderButtonStyles?: ButtonPresentationProps;
}

export const adiOrderDetailsReturnItemsModalStyles: ADIOrderDetailsReturnItemsModalStyles = {
    multipleOrdersButtonStyles: {
        variant: "secondary",
        css: css`
            display: inline-flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            width: 50%;
            height: 40px;
            max-width: 253px;
            border: solid 1px ${getColor("secondary")};
            background: ${getColor("common.background")};
            margin-right: 20px;
            &:focus{
                outline: none;
            }
            &:hover{
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            @media only screen and (max-width: 576px) {
                width: 100%;
                margin-right: 0px;
                max-width: unset;
            }
        `
    },
    singleOrderButtonStyles: {
        variant: "primary",
        css: css`
            display: inline-flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            width: 50%;
            height: 40px;
            max-width: 253px;
            color: ${getColor("common.background")};
            &:focus{
                outline: none;
            }
            @media only screen and (max-width: 576px) {
                width: 100%;
                margin-bottom: 20px;
                max-width: unset;
            }
        `
    },
    adiReturnItemsButtonContainer: {
        css: css`
            @media only screen and (max-width: 576px) {
                display: flex;
                flex-direction: column-reverse;
            }
        `,
    },
    adiReturnItemsBodyContainer: {
        css: css`
        overflow-y: auto;
        min-height: inherit;
        max-height: 62vh;
        padding-right: 18px;
        @media only screen and (max-width: 576px) {
            padding-right: 0px;
        }
        >${TypographyStyle}{
            margin-bottom: 25px;
        }
    `,
    },
    adiReturnItemsModal: {
        cssOverrides: {
            modalContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px;
                                height: 85vh !important;
                            `,
                            null,
                            css`
                                height: 75vh;
                            `,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalBody: css`
                border-radius: 6px;
                border: 1px solid ${getColor("common.border")};
                padding: 32px 10px 28px 25px;
                overflow-y: hidden;
                width: 582px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 18px;
                                margin: 0px !important;
                            `,
                            css`
                                height: auto;
                                margin: 45px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalTitle: css`
                border: none;
                padding: 0px;
                margin: 0 0 16px 0;
                > ${TypographyStyle} {
                    font-size: 34px;
                    font-weight: 900;
                    @media only screen and (max-width: 576px) {
                        font-size: 24px;
                    }
                }
                > ${ButtonWrapper} {
                    &:hover {
                        background: transparent;
                        border-color: transparent;
                    }
                    &:focus {
                        outline: none;
                    }
                    > ${IconWrapper} {
                        color: ${getColor("primary")};
                        width: 35px;
                        height: 35px;
                        > svg {
                            width: 35px;
                            height: 35px;
                            stroke-width: 1px;
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 0px;
            `,
        },
    },
};

const styles = adiOrderDetailsReturnItemsModalStyles;

const ADIOrderDetailsReturnItemsModal: FC<Props> = ({ isReturnItemsModalOpen, closeReturnModal, multipleOrders, singleOrder }) => {
    return (
        <Modal
            {...styles.adiReturnItemsModal}
            headline={translate("Return Items")}
            isOpen={isReturnItemsModalOpen}
            handleClose={() => closeReturnModal()}
        >
            <StyledWrapper {...styles.adiReturnItemsBodyContainer}>
                <Typography as="p">{translate("Do you want to return items from?")}</Typography>
                <StyledWrapper {...styles.adiReturnItemsButtonContainer}>
                    <Button {...styles.multipleOrdersButtonStyles} onClick={multipleOrders}>{translate("Multiple Orders")}</Button>
                    <Button {...styles.singleOrderButtonStyles} onClick={singleOrder}>{translate("This Order Only")}</Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    )
};

export default ADIOrderDetailsReturnItemsModal;
